/**
 * StudyGroupViewModal — Модальное окно просмотра учебной группы
 *
 * Показывает информацию о группе, расписание, список учеников.
 * Позволяет добавлять/убирать учеников (с проверкой конфликтов расписания).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Select,
  Button,
  Group,
  Stack,
  Text,
  Badge,
  Table,
  Alert,
  ActionIcon,
  Box,
  Divider,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconPencil,
  IconPlus,
  IconTrash,
  IconUsers,
  IconClock,
  IconCalendarTime,
  IconAlertTriangle,
  IconPlayerPause,
  IconPlayerPlay,
  IconUserPlus,
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import tokens from '../../../theme/designTokens';
import {
  getStudyGroup,
  enrollStudent,
  removeStudent,
} from '../../../services/api/studyGroupsService';
import { getStudents, createStudent } from '../../../services/api/studentsService';
import { useSession } from '../../../context/SessionContext';

const DAY_NAMES = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
const DAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  return timeStr.substring(0, 5);
};

const StudyGroupViewModal = ({
  opened,
  onClose,
  group: initialGroup,
  organizationId,
  onEdit,
  onPause,
  onStudentChange,
}) => {
  const { user } = useSession();
  const canManage = ['ADMIN', 'MANAGER'].includes(user?.role);

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  // Добавление ученика
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [studentOptions, setStudentOptions] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [enrollLoading, setEnrollLoading] = useState(false);

  // Создание нового ученика из формы
  const [createMode, setCreateMode] = useState(false);
  const [newStudent, setNewStudent] = useState({ first_name: '', last_name: '', date_of_birth: null });
  const [createLoading, setCreateLoading] = useState(false);

  // Загрузка актуальных данных группы
  const loadGroup = useCallback(async () => {
    if (!organizationId || !initialGroup?.id) return;
    try {
      setLoading(true);
      const data = await getStudyGroup(organizationId, initialGroup.id);
      setGroup(data);
    } catch (err) {
      console.error('Failed to load group:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, initialGroup?.id]);

  useEffect(() => {
    if (opened && initialGroup) {
      setGroup(initialGroup);
      loadGroup();
    }
  }, [opened, initialGroup, loadGroup]);

  // Загрузка списка учеников для добавления
  const loadStudents = useCallback(async () => {
    try {
      const data = await getStudents({ pageSize: 200, isActive: true });
      const enrolledIds = new Set((group?.students || []).map((s) => s.id));
      setStudentOptions(
        (data.items || [])
          .filter((s) => !enrolledIds.has(s.id))
          .map((s) => {
            const name = [s.last_name, s.first_name, s.middle_name].filter(Boolean).join(' ');
            // Формируем подпись с текущими группами и центрами
            const groups = s.study_groups || [];
            let description = '';
            if (groups.length > 0) {
              const parts = groups.map((g) =>
                `${g.name}${g.organization_name ? ` (${g.organization_name})` : ''}`
              );
              description = parts.join(', ');
            } else {
              description = 'Не записан в группы';
            }
            return {
              value: String(s.id),
              label: name,
              description,
            };
          })
      );
    } catch (err) {
      console.error('Failed to load students:', err);
    }
  }, [group?.students]);

  useEffect(() => {
    if (addStudentOpen) {
      loadStudents();
    }
  }, [addStudentOpen, loadStudents]);

  const handleEnroll = async () => {
    if (!selectedStudentId || !group) return;

    setEnrollLoading(true);
    try {
      await enrollStudent(organizationId, group.id, parseInt(selectedStudentId));
      notifications.show({
        title: 'Успешно',
        message: 'Ученик записан в группу',
        color: 'green',
      });
      setAddStudentOpen(false);
      setSelectedStudentId(null);
      await loadGroup();
      if (onStudentChange) onStudentChange();
    } catch (err) {
      if (err.conflicts) {
        // Показать детали конфликта
        const conflictText = err.conflicts
          .map(
            (c) =>
              `${c.day_name}: ${c.existing_time} (${c.existing_duration} мин) — группа «${c.conflicting_group_name}» пересекается с ${c.new_time} (${c.new_duration} мин)`
          )
          .join('\n');
        notifications.show({
          title: 'Конфликт расписания',
          message: conflictText,
          color: 'orange',
          autoClose: 10000,
        });
      } else {
        notifications.show({
          title: 'Ошибка',
          message: err.message,
          color: 'red',
        });
      }
    } finally {
      setEnrollLoading(false);
    }
  };

  // Создать нового ученика и сразу записать в группу
  const handleCreateAndEnroll = async () => {
    if (!newStudent.first_name.trim() || !group) return;

    setCreateLoading(true);
    try {
      // Создаём ученика
      const studentData = {
        first_name: newStudent.first_name.trim(),
        ...(newStudent.last_name?.trim() && { last_name: newStudent.last_name.trim() }),
        ...(newStudent.date_of_birth && { date_of_birth: newStudent.date_of_birth.toISOString().split('T')[0] }),
      };
      const created = await createStudent(studentData);

      // Записываем в группу
      try {
        await enrollStudent(organizationId, group.id, created.id);
        notifications.show({
          title: 'Успешно',
          message: `Ученик «${created.full_name || created.first_name}» создан и записан в группу`,
          color: 'green',
        });
      } catch (enrollErr) {
        // Ученик создан, но запись не удалась (конфликт расписания)
        if (enrollErr.conflicts) {
          const conflictText = enrollErr.conflicts
            .map((c) => `${c.day_name}: ${c.existing_time} — группа «${c.conflicting_group_name}»`)
            .join('\n');
          notifications.show({
            title: 'Ученик создан, но есть конфликт расписания',
            message: conflictText,
            color: 'orange',
            autoClose: 10000,
          });
        } else {
          notifications.show({
            title: 'Ученик создан, но не записан в группу',
            message: enrollErr.message,
            color: 'orange',
          });
        }
      }

      // Сбросить форму
      setCreateMode(false);
      setAddStudentOpen(false);
      setNewStudent({ first_name: '', last_name: '', date_of_birth: null });
      await loadGroup();
      if (onStudentChange) onStudentChange();
    } catch (err) {
      notifications.show({
        title: 'Ошибка создания ученика',
        message: err.message,
        color: 'red',
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleRemoveStudent = (student) => {
    modals.openConfirmModal({
      title: 'Убрать ученика',
      centered: true,
      children: (
        <Text size="sm">
          Убрать «{student.full_name || student.first_name}» из группы «{group?.name}»?
        </Text>
      ),
      labels: { confirm: 'Убрать', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await removeStudent(organizationId, group.id, student.id);
          notifications.show({
            title: 'Успешно',
            message: 'Ученик убран из группы',
            color: 'green',
          });
          await loadGroup();
          if (onStudentChange) onStudentChange();
        } catch (err) {
          notifications.show({
            title: 'Ошибка',
            message: err.message,
            color: 'red',
          });
        }
      },
    });
  };

  if (!group) return null;

  const students = group.students || [];
  const schedules = group.schedules || [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Text fw={600}>{group.name}</Text>
          {!group.is_active && (
            <Badge size="sm" color="orange" variant="filled" radius="xl" leftSection={<IconPlayerPause size={12} />}>
              На паузе
            </Badge>
          )}
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Информация о группе */}
        <Group gap="lg" wrap="wrap">
          {group.subject_name && (
            <Box>
              <Text size="xs" c="dimmed">Предмет</Text>
              <Badge color="blue" variant="filled" radius="xl" size="md">
                {group.subject_name}
              </Badge>
            </Box>
          )}
          {group.teacher_name && (
            <Box>
              <Text size="xs" c="dimmed">Педагог</Text>
              <Text size="sm" fw={500}>{group.teacher_name}</Text>
            </Box>
          )}
          <Box>
            <Text size="xs" c="dimmed">Продолжительность</Text>
            <Group gap={4}>
              <IconClock size={14} color={tokens.colors.neutral[6]} />
              <Text size="sm" fw={500}>{group.duration_minutes} мин</Text>
            </Group>
          </Box>
          {group.max_students && (
            <Box>
              <Text size="xs" c="dimmed">Макс. учеников</Text>
              <Text size="sm" fw={500}>{group.max_students}</Text>
            </Box>
          )}
        </Group>

        {/* Расписание */}
        <Box>
          <Text size="sm" fw={600} mb="xs">
            <Group gap={4}>
              <IconCalendarTime size={16} />
              Расписание
            </Group>
          </Text>
          <Group gap="xs">
            {schedules.map((s, i) => (
              <Badge key={i} variant="filled" color="indigo" radius="xl" size="lg">
                {DAY_SHORT[s.day_of_week]} {formatTime(s.start_time)}
                {s.start_date && ` (с ${new Date(s.start_date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}г)`}
              </Badge>
            ))}
          </Group>
        </Box>

        {/* Заметки */}
        {group.notes && (
          <Box>
            <Text size="xs" c="dimmed">Заметки</Text>
            <Text size="sm">{group.notes}</Text>
          </Box>
        )}

        <Divider />

        {/* Ученики */}
        <Box>
          <Group justify="space-between" mb="sm">
            <Text size="sm" fw={600}>
              <Group gap={4}>
                <IconUsers size={16} />
                Ученики ({students.length})
              </Group>
            </Text>
            {canManage && (
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => setAddStudentOpen(true)}
              >
                Добавить
              </Button>
            )}
          </Group>

          {/* Форма добавления ученика */}
          {addStudentOpen && (
            <Box mb="sm" p="sm" style={{
              borderRadius: 12,
              border: `1px solid ${tokens.colors.neutral[3]}`,
              backgroundColor: tokens.colors.neutral[1],
            }}>
              {!createMode ? (
                <>
                  <Group gap="sm">
                    <Select
                      placeholder="Начните вводить имя ученика..."
                      data={studentOptions}
                      value={selectedStudentId}
                      onChange={setSelectedStudentId}
                      searchable
                      renderOption={({ option }) => (
                        <Box>
                          <Text size="sm" fw={500}>{option.label}</Text>
                          {option.description && (
                            <Text size="xs" c="dimmed">{option.description}</Text>
                          )}
                        </Box>
                      )}
                      nothingFoundMessage={
                        <Box
                          p="xs"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setCreateMode(true)}
                        >
                          <Group gap={6}>
                            <IconUserPlus size={16} color={tokens.colors.brand.primary} />
                            <Text size="sm" c={tokens.colors.brand.primary} fw={500}>
                              Создать нового ученика
                            </Text>
                          </Group>
                        </Box>
                      }
                      style={{ flex: 1 }}
                      radius="xl"
                    />
                    <Button
                      size="sm"
                      onClick={handleEnroll}
                      loading={enrollLoading}
                      disabled={!selectedStudentId}
                    >
                      Записать
                    </Button>
                    <Button
                      variant="subtle"
                      size="sm"
                      color="gray"
                      onClick={() => {
                        setAddStudentOpen(false);
                        setSelectedStudentId(null);
                      }}
                    >
                      Отмена
                    </Button>
                  </Group>
                  <Group mt="xs" gap={4}>
                    <IconUserPlus size={14} color={tokens.colors.neutral[6]} />
                    <Text
                      size="xs"
                      c={tokens.colors.brand.primary}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setCreateMode(true)}
                    >
                      Создать нового ученика
                    </Text>
                  </Group>
                </>
              ) : (
                /* Мини-форма создания нового ученика */
                <Stack gap="xs">
                  <Group gap={6} mb={4}>
                    <IconUserPlus size={16} color={tokens.colors.brand.primary} />
                    <Text size="sm" fw={600}>Новый ученик</Text>
                  </Group>
                  <Group gap="sm" grow>
                    <TextInput
                      placeholder="Имя *"
                      value={newStudent.first_name}
                      onChange={(e) => setNewStudent({ ...newStudent, first_name: e.target.value })}
                      radius="xl"
                      size="sm"
                    />
                    <TextInput
                      placeholder="Фамилия"
                      value={newStudent.last_name}
                      onChange={(e) => setNewStudent({ ...newStudent, last_name: e.target.value })}
                      radius="xl"
                      size="sm"
                    />
                    <DatePickerInput
                      placeholder="Дата рождения"
                      value={newStudent.date_of_birth}
                      onChange={(val) => setNewStudent({ ...newStudent, date_of_birth: val })}
                      radius="xl"
                      size="sm"
                      maxDate={new Date()}
                      locale="ru"
                      clearable
                      valueFormat="DD.MM.YYYY"
                    />
                  </Group>
                  <Group gap="sm" justify="flex-end">
                    <Button
                      variant="subtle"
                      size="sm"
                      color="gray"
                      onClick={() => {
                        setCreateMode(false);
                        setNewStudent({ first_name: '', last_name: '', date_of_birth: null });
                      }}
                    >
                      Назад к поиску
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleCreateAndEnroll}
                      loading={createLoading}
                      disabled={!newStudent.first_name.trim()}
                      leftSection={<IconUserPlus size={14} />}
                    >
                      Создать и записать
                    </Button>
                  </Group>
                </Stack>
              )}
            </Box>
          )}

          {students.length > 0 ? (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Имя</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  {canManage && <Table.Th w={50}></Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {students.map((student) => (
                  <Table.Tr key={student.id}>
                    <Table.Td>
                      <Text size="sm">
                        {student.full_name || student.first_name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="xs"
                        color={student.is_active ? 'green' : 'gray'}
                        variant="filled"
                        radius="xl"
                      >
                        {student.is_active ? 'Активен' : 'Неактивен'}
                      </Badge>
                    </Table.Td>
                    {canManage && (
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => handleRemoveStudent(student)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="md">
              В группе пока нет учеников
            </Text>
          )}
        </Box>

        {/* Кнопки */}
        <Group justify="flex-end" mt="sm">
          {onEdit && (
            <Button
              variant="light"
              leftSection={<IconPencil size={14} />}
              onClick={() => onEdit(group)}
            >
              Редактировать
            </Button>
          )}
          {onPause && (
            <Button
              variant="light"
              color={group.is_active ? 'orange' : 'green'}
              leftSection={group.is_active ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
              onClick={() => onPause(group)}
            >
              {group.is_active ? 'На паузу' : 'Возобновить'}
            </Button>
          )}
          <Button variant="default" onClick={onClose}>
            Закрыть
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StudyGroupViewModal;
